/**
 * Receipt allocation editor — `/finance/customer-receipts/$receiptId`.
 *
 * The PUT body carries the COMPLETE intended set of applications, not a delta,
 * so what the clerk sees on screen is exactly what is stored. This screen is
 * therefore built as a full editor over the customer's open invoices, seeded
 * from nothing and sent whole.
 *
 * A remainder is not an error. Partial settlement leaves an invoice
 * `partially_paid`, exact settlement closes it, and anything left over is an
 * overpayment that lands on the customer's account and comes back as
 * `on_account_amount`. The two genuine refusals — allocating past the money
 * received (`ar.allocation_exceeds_receipt`) and past an invoice's open amount
 * (`ar.allocation_exceeds_invoice`) — are caught here in exact decimal before
 * the request goes out, because they are certain rejections, not judgements.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { ArrowLeft } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";

import { allocateCustomerReceipt, type AllocateReceiptRequest, type AllocationPlan } from "../api";
import {
  AMOUNT_SCALE,
  compareDecimals,
  isZeroDecimal,
  normalizeDecimal,
  subtractDecimals,
  sumDecimals,
} from "../decimal";
import {
  DecimalField,
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
} from "../components/fields";
import {
  financeKeys,
  useCustomerInvoiceList,
  useCustomerReceipt,
  usePartyOptions,
} from "../queries";

export interface CustomerReceiptPageProps {
  receiptId?: string;
  onBack: () => void;
  onOpenInvoice?: (invoiceId: string) => void;
  canAllocate?: boolean;
  canReadInvoices?: boolean;
  canReadParties?: boolean;
}

export function CustomerReceiptPage({
  receiptId,
  onBack,
  onOpenInvoice,
  canAllocate = true,
  canReadInvoices = true,
  canReadParties = true,
}: CustomerReceiptPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const receiptQuery = useCustomerReceipt(receiptId);
  const receipt = receiptQuery.data;

  const partiesQuery = usePartyOptions("customer", canReadParties);
  const invoicesQuery = useCustomerInvoiceList(
    receipt === undefined ? {} : { party_id: receipt.party_id },
    canReadInvoices && receipt !== undefined,
  );

  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<AllocationPlan | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const partyName = useMemo(() => {
    if (receipt === undefined) return "";
    return (
      (partiesQuery.data ?? []).find((party) => party.id === receipt.party_id)?.display_name ??
      receipt.party_id
    );
  }, [receipt, partiesQuery.data]);

  /** Only invoices that still owe something can absorb an allocation. */
  const openInvoices = useMemo(
    () =>
      (invoicesQuery.data ?? []).filter(
        (invoice) =>
          (invoice.status === "posted" || invoice.status === "partially_paid") &&
          !isZeroDecimal(invoice.open_amount.amount, AMOUNT_SCALE),
      ),
    [invoicesQuery.data],
  );

  const allocateMutation = useMutation({
    mutationFn: (input: { receiptId: string; body: AllocateReceiptRequest }) =>
      allocateCustomerReceipt(input.receiptId, input.body),
    onSuccess: (result) => {
      setPlan(result);
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerReceipts.all });
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerInvoices.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const enteredTotal = useMemo(() => {
    const values: string[] = [];
    for (const value of Object.values(amounts)) {
      const normalized = normalizeDecimal(value, { scale: AMOUNT_SCALE });
      if (normalized !== null) values.push(normalized);
    }
    return sumDecimals(values, AMOUNT_SCALE);
  }, [amounts]);

  if (receiptQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t("app.loading", "Loading…")}</div>;
  }

  if (receipt === undefined) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <ProblemBanner>
          {t("finance.receipts.notFound", "This receipt could not be loaded.")}
        </ProblemBanner>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("finance.receipts.back", "Back")}
        </Button>
      </div>
    );
  }

  const remainder = subtractDecimals(receipt.amount.amount, enteredTotal, AMOUNT_SCALE);
  const overAllocated = compareDecimals(enteredTotal, receipt.amount.amount, AMOUNT_SCALE) > 0;

  const submit = () => {
    const allocations: { invoice_id: string; amount: string }[] = [];
    for (const invoice of openInvoices) {
      const raw = amounts[invoice.id] ?? "";
      if (raw.trim() === "") continue;
      const value = normalizeDecimal(raw, { scale: AMOUNT_SCALE, allowZero: false });
      if (value === null) {
        setFormError(
          t(
            "finance.receipts.allocationInvalid",
            "Enter allocation amounts with at most four decimals.",
          ),
        );
        return;
      }
      if (compareDecimals(value, invoice.open_amount.amount, AMOUNT_SCALE) > 0) {
        setFormError(
          t(
            "finance.receipts.exceedsInvoice",
            "Allocating {{amount}} to {{invoice}} exceeds its open amount.",
            { amount: value, invoice: invoice.invoice_no },
          ),
        );
        return;
      }
      allocations.push({ invoice_id: invoice.id, amount: value });
    }
    if (overAllocated) {
      setFormError(
        t(
          "finance.receipts.exceedsReceipt",
          "The allocations add up to more than the money received.",
        ),
      );
      return;
    }
    setFormError(null);
    allocateMutation.mutate({
      receiptId: receipt.id,
      body: { currency: receipt.currency, allocations },
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={
          receipt.receipt_no === ""
            ? t("finance.receipts.draftTitle", "Draft receipt")
            : receipt.receipt_no
        }
        description={partyName}
        actions={
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden="true" />
            {t("finance.receipts.back", "Back")}
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
          <DefinitionList
            rows={[
              {
                label: t("finance.receipts.amount", "Amount received"),
                value: <MoneyDisplay value={receipt.amount} />,
              },
              {
                label: t("finance.receipts.allocatedAmount", "Allocated"),
                value: <MoneyDisplay value={receipt.allocated_amount} />,
              },
              {
                label: t("finance.receipts.onAccount", "On account"),
                value: <MoneyDisplay value={receipt.on_account_amount} />,
              },
              {
                label: t("finance.receipts.receivedOn", "Received on"),
                value: <span dir="ltr">{receipt.received_on}</span>,
              },
              {
                label: t("finance.receipts.method", "Method"),
                value: t(`finance.receipts.methodOption.${receipt.method}`, receipt.method),
              },
              {
                label: t("finance.receipts.reference", "Reference"),
                value: <span dir="ltr">{receipt.reference}</span>,
              },
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.receipts.allocationSection", "Allocate against open invoices")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.receipts.allocationHint",
              "This is the whole plan, not a change to it. Anything you leave unallocated lands on the customer's account.",
            )}
          </p>

          {openInvoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.receipts.noOpenInvoices", "This customer has no open invoices.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th scope="col" className="p-2 text-start">
                      {t("finance.receipts.invoice", "Invoice")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("finance.receipts.dueDate", "Due")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("finance.receipts.openAmount", "Open")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("finance.receipts.allocate", "Allocate")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {openInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b align-top">
                      <td className="p-2">
                        {onOpenInvoice === undefined ? (
                          <span dir="ltr" className="font-mono">
                            {invoice.invoice_no}
                          </span>
                        ) : (
                          <button
                            type="button"
                            dir="ltr"
                            className="font-mono underline-offset-4 hover:underline"
                            onClick={() => onOpenInvoice(invoice.id)}
                          >
                            {invoice.invoice_no}
                          </button>
                        )}
                      </td>
                      <td dir="ltr" className="p-2">
                        {invoice.due_date}
                      </td>
                      <td className="p-2 text-end">
                        <MoneyDisplay value={invoice.open_amount} />
                      </td>
                      <td className="p-2">
                        <div className="flex items-end justify-end gap-2">
                          <DecimalField
                            id={`allocate-${invoice.id}`}
                            label={t("finance.receipts.allocateTo", "Allocate to {{invoice}}", {
                              invoice: invoice.invoice_no,
                            })}
                            value={amounts[invoice.id] ?? ""}
                            onChange={(value) =>
                              setAmounts((current) => ({ ...current, [invoice.id]: value }))
                            }
                            decimal={{ scale: AMOUNT_SCALE, allowZero: false }}
                            className="w-40"
                            invalidMessage={t(
                              "finance.receipts.allocationInvalid",
                              "Enter allocation amounts with at most four decimals.",
                            )}
                            disabled={!canAllocate}
                          />
                          {canAllocate ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setAmounts((current) => ({
                                  ...current,
                                  [invoice.id]: invoice.open_amount.amount,
                                }))
                              }
                            >
                              {t("finance.receipts.settleInFull", "Settle in full")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-6 border-t pt-4 text-sm">
            <span data-testid="allocation-entered-total">
              {t("finance.receipts.enteredTotal", "Allocating")}{" "}
              <MoneyDisplay value={{ amount: enteredTotal, currency: receipt.currency }} />
            </span>
            <span data-testid="allocation-remainder">
              {t("finance.receipts.remainder", "On account after this")}{" "}
              <MoneyDisplay
                value={{
                  amount: overAllocated ? "0" : remainder,
                  currency: receipt.currency,
                }}
              />
            </span>
          </div>

          {overAllocated ? (
            <ProblemBanner>
              {t(
                "finance.receipts.exceedsReceipt",
                "The allocations add up to more than the money received.",
              )}
            </ProblemBanner>
          ) : isZeroDecimal(remainder, AMOUNT_SCALE) ? null : (
            <NoticeBanner>
              {t(
                "finance.receipts.overpaymentNotice",
                "The remainder is an overpayment on the customer's account — a legitimate outcome, not an error.",
              )}
            </NoticeBanner>
          )}

          {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

          {canAllocate ? (
            <div>
              <Button type="button" loading={allocateMutation.isPending} onClick={submit}>
                {t("finance.receipts.saveAllocation", "Save allocation")}
              </Button>
            </div>
          ) : null}

          {plan === null ? null : (
            <div className="rounded-md border p-3 text-sm" data-testid="allocation-plan">
              <p className="font-medium">{t("finance.receipts.planTitle", "Stored allocation")}</p>
              <p className="text-muted-foreground">
                {t("finance.receipts.planApplied", "Applied")}{" "}
                <MoneyDisplay value={plan.applied_total} /> ·{" "}
                {t("finance.receipts.planOnAccount", "on account")}{" "}
                <MoneyDisplay value={plan.on_account_amount} />
              </p>
              <ul className="mt-2 flex flex-col gap-1">
                {plan.allocations.map((allocation) => (
                  <li key={allocation.invoice_id} className="text-muted-foreground">
                    <MoneyDisplay value={allocation.amount} /> ·{" "}
                    {allocation.fully_settles
                      ? t("finance.receipts.planSettled", "settles the invoice")
                      : t("finance.receipts.planRemaining", "leaves {{amount}} open", {
                          amount: allocation.remaining_open.amount,
                        })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
